// react
import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

// components
import {
  ActionPlanHeader,
  Button,
  Card,
  Checkbox,
  Container,
  CSAT,
  Dialog,
  LoadingTransition,
  LottieAnimation,
  MemphisVariant,
  Skeleton,
} from '@/components/atoms';
import { SanityContent } from '@/components/organisms';
import { Icon, validateIcon } from '@/components/atoms/Icon';

// hooks
import { useWindowSize } from '@/hooks';

// theme
import { theme } from '@/theme';

// models
import {
  ActionPlanCategory,
  ActionPlanItem,
  Content as ActionPlanSanityContent,
} from '@karehero/models';

// assets
// @transform-path ../../../../../../src/assets/animation/success.json
import successAnimation from '@/assets/animation/success.json';

interface ActionPlanContentProps {
  className?: string;
  actionPlanItem?: ActionPlanItem;
  categories?: ActionPlanCategory[];
  headerImageUrl?: string;
  sanityContent?: ActionPlanSanityContent[];
  isHideBack?: boolean;
  onComplete?: (actionPlanItemId: string) => void;
  onCSAT?: (value: number) => void;
  isLoading?: boolean;
}

/**
 * ActionPlanContent shows the content of the action plan along with a
 * "Get Started" button and a "Mark as complete" checkbox which will trigger
 * a celebration animation when checked.
 */
export const ActionPlanContent = ({
  className,
  actionPlanItem,
  categories,
  headerImageUrl,
  sanityContent,
  isHideBack,
  onComplete,
  onCSAT,
  isLoading = true,
}: ActionPlanContentProps) => {
  // state
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // hooks
  const [width] = useWindowSize();

  // memos
  const category = useMemo(() => {
    return categories?.find(
      (x) => x.id === actionPlanItem?.actionPlanCategoryID,
    );
  }, [categories, actionPlanItem]);

  const isCSAT = useMemo(() => {
    return actionPlanItem?.isComplete && !actionPlanItem?.isFeedback;
  }, [actionPlanItem]);

  return (
    <>
      <LoadingTransition
        isLoading={isLoading}
        loading={<Loading />}
        content={
          <Wrapper className={className}>
            <ActionPlanHeader
              color={category?.color || 'grey'}
              isBack={!isHideBack}
              memphis={category?.headerMemphis as MemphisVariant}
            />
            <Container size='md'>
              <Main>
                <Header>
                  <StyledTitle>{actionPlanItem?.title}</StyledTitle>
                  <Category>
                    {category?.icon && (
                      <IconWrapper color={category?.color || 'grey'}>
                        <Icon
                          icon={validateIcon(category?.icon)}
                          fontSize={16}
                        />
                      </IconWrapper>
                    )}
                    <StyledSubTitle>{actionPlanItem?.subTitle}</StyledSubTitle>
                  </Category>
                </Header>
                <Content>
                  <SanityContent content={sanityContent} />
                </Content>
                <Footer>
                  <FooterWrapper>
                    {actionPlanItem?.actionType === 'pathway' && (
                      <Button
                        variant='primary'
                        ariaLabel={
                          actionPlanItem?.route?.includes('/book-call')
                            ? 'book a call'
                            : 'continue'
                        }
                        to={actionPlanItem?.route}
                        isFullWidth
                        onPress={() => {
                          if (actionPlanItem?.isFirst) {
                            onComplete?.(actionPlanItem?.id);
                          }
                        }}
                      >
                        {actionPlanItem?.route?.includes('/book-call')
                          ? 'Book A Call'
                          : 'Continue'}
                      </Button>
                    )}

                    {actionPlanItem?.isModify && (
                      <CompletedCheckbox
                        $isComplete={actionPlanItem?.isComplete}
                      >
                        <Checkbox
                          label={
                            actionPlanItem?.isComplete
                              ? 'Completed'
                              : 'Mark as complete'
                          }
                          size='md'
                          variant='circle'
                          value={actionPlanItem?.isComplete}
                          onChange={() => {
                            if (!actionPlanItem || actionPlanItem.isComplete) {
                              return;
                            }
                            setIsDialogOpen(true);
                            onComplete?.(actionPlanItem?.id);
                          }}
                        />
                      </CompletedCheckbox>
                    )}
                    {isCSAT && (
                      <StyledCard
                        elevation={0}
                        isPaddingSm={width < theme.breakpoint.smValue}
                      >
                        <CSAT onPress={onCSAT} />
                      </StyledCard>
                    )}
                  </FooterWrapper>
                </Footer>
              </Main>
            </Container>
          </Wrapper>
        }
      />
      <Dialog
        variant='sheet'
        direction='bottom'
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
      >
        <Celebration>
          <CelebrationHeader>
            <AnimationContainer>
              <LottieAnimation
                src={successAnimation}
                loop={true}
                autoplay={true}
              />
            </AnimationContainer>
            <CelebrationIconWrapper>
              <Icon icon='party-horn' fontSize={72} />
            </CelebrationIconWrapper>
          </CelebrationHeader>
          <CelebrationText>
            Nice work on completing your action!
          </CelebrationText>
          {isHideBack && (
            <StyledButton
              size='md'
              isFullWidth={true}
              ariaLabel={'See my next actions'}
              to={'/action-plan'}
            >
              See my next actions
            </StyledButton>
          )}
        </Celebration>
      </Dialog>
    </>
  );
};

const Loading = () => {
  return (
    <Wrapper>
      <Skeleton shape='title' isSquare isMargin={false} height='50px' />
      <Container size='md'>
        <Main>
          <Header>
            <Skeleton shape='text' />
            <Category>
              <Skeleton
                shape='avatar'
                width='32px'
                height='32px'
                isMargin={false}
              />
              <Skeleton shape='text' />
            </Category>
          </Header>
          <Content>
            <Skeleton shape='header-image' isRounded={false} height='200px' />
            <Skeleton shape='paragraph' />
            <Skeleton shape='paragraph' />
          </Content>
          <Footer>
            <FooterWrapper>
              <Skeleton shape='button' width='100%' />
              <Skeleton shape='text' />
            </FooterWrapper>
          </Footer>
        </Main>
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  height: 100%;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  min-height: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[4]};
`;

const Content = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  gap: ${(props) => props.theme.spacing[16]};
  flex: 1;
  padding-bottom: ${(props) => props.theme.spacing[24]};
  padding-top: ${(props) => props.theme.spacing[16]};
  position: sticky;
  bottom: 0;
  background: ${(props) => props.theme.color.neutral[10]};
`;

const StyledTitle = styled.div`
  color: ${(props) => props.theme.color.neutral[90]};
  font: ${(props) => props.theme.typography.body.md.medium};
`;

const Category = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
`;

const StyledSubTitle = styled.div`
  color: ${(props) => props.theme.color.neutral[90]};
  font: ${(props) => props.theme.typography.body.md.regular};
`;

const IconWrapper = styled.div<{
  color: string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props.theme.spacing[4]};
  border-radius: 99999px;
  width: 32px;
  min-width: 32px;
  max-width: 32px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  background: ${(props) =>
    props.theme.color.actionPlanCards[props.color]?.secondary};
  color: ${(props) => props.theme.color.actionPlanCards[props.color]?.primary};
`;

interface CompletedCheckboxProps {
  $isComplete?: boolean;
}
const CompletedCheckbox = styled.div<CompletedCheckboxProps>`
  display: flex;
  justify-content: center;
  ${(props) =>
    props.$isComplete &&
    css`
      pointer-events: none;
    `}
`;

const Celebration = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.spacing[16]};
  padding: ${(props) => props.theme.spacing[48]} 0;
`;

const CelebrationText = styled.div`
  color: ${(props) => props.theme.color.neutral[90]};
  font: ${(props) => props.theme.typography.body.lg.regular};
  text-align: center;
`;

const CelebrationHeader = styled.div`
  height: 200px;
  width: 200px;
  position: relative;
`;

const CelebrationIconWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${(props) => props.theme.color.primary[70]};
`;

const AnimationContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: 180%;
  width: 180%;
  position: absolute;
`;

const FooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  width: 100%;
  max-width: 400px;
  justify-content: center;
  align-items: center;
`;

const StyledCard = styled(Card)`
  margin-top: ${(props) => props.theme.spacing[8]};
  width: 100%;
`;

const StyledButton = styled(Button)`
  max-width: 400px;
`;

ActionPlanContent.displayName = 'ActionPlanContent';

export default ActionPlanContent;
